import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import getAPIbaseURL from "../../../utils/helpers";
import { useDispatch, useSelector } from 'react-redux';
import { fetchNextShift, fetchShifts } from '../../../redux/slices/shiftSlice';
import { fetchAbsences } from '../../../redux/slices/absenceSlice';
import { fetchUsers } from '../../../redux/slices/userSlice';
import { getCurrentUser } from "../../../redux/slices/authSlice";
import { Row, Col, Image, Card } from "react-bootstrap";
import BarChart from '../BarChart/BarChart';
import ClockCard from '../ClockCard/ClockCard';
import { formatTimeRange } from '../../../utils/timeFormat';
import UserInitialsAvatar from '../../../utils/userInitialAvatar';

import {
    FaBell, FaClock, FaCalendar, FaCheck,
    FaUsers, FaAngleDown, FaPencilAlt, FaInfoCircle
} from 'react-icons/fa';
import { FaLocationDot } from "react-icons/fa6";

const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${getAPIbaseURL()}${path}`;
};

const MobileHome = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const [shiftInfo, setShiftInfo] = useState(null);
    
    const currentUser = useSelector((state) => state.auth.user);
    const shifts = useSelector((state) => state.shifts.items);
    const absences = useSelector((state) => state.absences.items);
    const users = useSelector((state) => state.users.items);

    useEffect(() => {
        if (!currentUser) {
            dispatch(getCurrentUser());
        }
        
        dispatch(fetchShifts());
        dispatch(fetchAbsences());
        dispatch(fetchUsers());

        dispatch(fetchNextShift())
            .unwrap()
            .then(setShiftInfo)
            .catch((error) => console.error('Erro ao buscar próximo turno:', error));
            
    }, [dispatch, currentUser]);

    const notWorkingEmployees = useMemo(() => {
        if (!shifts || !absences || !users) return [];

        const todayDate = new Date().toLocaleDateString('pt-PT');
        const usersMap = new Map();

        users.forEach(user => {
            usersMap.set(user.id, {
                id: user.id,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
                first_name: user.first_name,
                last_name: user.last_name,
                profile_picture: getImageUrl(user.profile_picture),
            });
        });

        const workingToday = new Set();
        shifts.forEach(shift => {
            const shiftDate = new Date(shift.start_time).toLocaleDateString('pt-PT');
            if (shiftDate === todayDate && shift.start_time !== 'Ausência') {
                workingToday.add(shift.user_id || shift.id);
            }
        });

        const absentToday = new Set();
        absences.forEach(absence => {
            const absenceDate = new Date(absence.date).toLocaleDateString('pt-PT');
            if (absenceDate === todayDate && absence.status === 'approved') {
                absentToday.add(absence.user_id);
            }
        });

        const notWorking = [];
        usersMap.forEach((user, id) => {
            if (!workingToday.has(id) || absentToday.has(id)) {
                notWorking.push(user);
            }
        });

        return notWorking;
    }, [shifts, absences, users]);

    const handleNotificationClick = () => navigate("/notifications");

    const doubleCardStyles = { width: "48%", flex: "0 0 48%", maxWidth: "48%" };
    const imageCardStyles = { width: '3.5vw', height: '3.5vw', minWidth: '20px', minHeight: '20px' };

    return (
        <div style={{ background: 'linear-gradient(20deg, rgba(30, 3, 66, 1) 20%, rgb(6, 141, 199) 80%)', minHeight: "100dvh" }} className='custom-scroll pb-4'>
            <Row className='p-4'>
                <Col className='d-flex justify-content-center align-items-start col-auto'>
                    {currentUser?.profile_picture ? (
                        <Image src={getImageUrl(currentUser.profile_picture)} roundedCircle width={45} height={45} />
                    ) : (
                        <UserInitialsAvatar first_name={currentUser?.first_name} last_name={currentUser?.last_name} style={{ width: 45, height: 45 }} />
                    )}
                </Col>
                <Col className='d-flex justify-content-start align-items-center flex-grow-1'>
                    <span style={{ color: "#ffffff", fontWeight: "500" }} className='fs-5'>
                        Bem-vindo, <b>{currentUser?.username || "User"}</b>
                    </span>
                </Col>
                <Col className="d-flex justify-content-end align-items-center col-auto">
                    <FaBell style={{ color: "white", cursor: "pointer" }} className='fs-2' onClick={handleNotificationClick} />
                </Col>
            </Row>

            <Row className='d-flex justify-content-center'>
                <Col className='col-11'>
                    <ClockCard />
                </Col>
            </Row>

            <Row className='d-flex justify-content-center mt-3 gx-2'>
                <Col className='col' style={{ ...doubleCardStyles }}>
                    <Card className='rounded-4 shadow h-100' style={{ backgroundColor: "#17296B", color: "white" }}>
                        <Card.Body className='p-2'>
                            <Row>
                                <Col xs={12} className='d-flex justify-content-start align-items-center gap-2 pb-2'>
                                    <FaClock className='fs-4' />
                                    <b style={{ fontSize: "15px" }}>Próximo Turno</b>
                                </Col>
                            </Row>
                            <Row className='d-flex justify-content-center flex-column gap-2'>
                                <Col xs={12} className='d-flex align-items-center gap-2 mt-2'>
                                    <FaLocationDot className='fs-5' />
                                    <b style={{ fontSize: "14px" }}>{shiftInfo?.work_location || "-"}</b>
                                </Col>
                                <Col xs={12} className='d-flex align-items-center gap-2'>
                                    <FaClock className='fs-4' />
                                    <b style={{ fontSize: "14px" }}>
                                        {shiftInfo ? formatTimeRange(shiftInfo.start_time, shiftInfo.end_time) : "-"}
                                    </b>
                                </Col>
                                <Col xs={12} className='d-flex align-items-center gap-2'>
                                    <FaCalendar className='fs-5' />
                                    <b style={{ fontSize: "14px" }}>
                                        {shiftInfo ? new Date(shiftInfo.start_time).toLocaleDateString("pt-PT", { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
                                    </b>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col className='col' style={{ ...doubleCardStyles }}>
                    <Card className='rounded-4 shadow h-100' style={{ backgroundColor: "#17296B" }}>
                        <Card.Body className='p-2'>
                            <Row>
                                <Col className='d-flex justify-content-start align-items-center gap-2 pb-2 text-white'>
                                    <FaUsers className='fs-4' />
                                    <b style={{ fontSize: "14px" }}>Fora de Serviço</b>
                                </Col>
                            </Row>
                            <Row className='d-flex justify-content-center flex-column mt-1 gap-1 text-white'>
                                {notWorkingEmployees.length > 0 ? (
                                    <>
                                        {notWorkingEmployees.slice(0, 3).map((employee) => (
                                            <Col key={employee.id} className='col-12 d-flex align-items-center gap-2'>
                                                {employee.profile_picture ? <Image src={employee.profile_picture} roundedCircle style={{ ...imageCardStyles }} /> : <UserInitialsAvatar first_name={employee.first_name} last_name={employee.last_name} style={imageCardStyles} />}
                                                <b style={{ fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "120px" }} title={employee.name}>
                                                    {employee.name}
                                                </b>
                                            </Col>
                                        ))}

                                        <Col className='col-12 d-flex align-items-center gap-1 mt-1'>
                                            {notWorkingEmployees.slice(3, 7).map((employee) => (
                                                employee.profile_picture ? <Image key={employee.id} src={employee.profile_picture} roundedCircle style={{ ...imageCardStyles }} /> : <UserInitialsAvatar key={employee.id} first_name={employee.first_name} last_name={employee.last_name} style={imageCardStyles} />
                                            ))}
                                            {notWorkingEmployees.length > 7 && (
                                                <b style={{ fontSize: "16px" }}>+{notWorkingEmployees.length - 7}</b>
                                            )}
                                        </Col>
                                    </>
                                ) : (
                                    <Col className='col-12'>
                                        <span style={{ fontSize: "13px" }}>Equipa toda disponível.</span>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='d-flex justify-content-center'>
                <Col className='col-11 mt-3'>
                    <Card className='rounded-4 shadow' style={{ backgroundColor: "#17296B" }}>
                        <Card.Body className='p-3'>
                            <Row>
                                <Col className='d-flex align-items-center gap-2 col-auto text-white'>
                                    <b style={{ fontSize: "3.5vmin" }}>Estatística do Ano</b>
                                    <FaAngleDown className='fs-4' />
                                </Col>
                            </Row>
                            <Row className='d-flex justify-content-center align-items-center mt-2'>
                                <Col className='d-flex justify-content-center'>
                                    <BarChart />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='d-flex justify-content-center mt-3 gx-2'>
                <Col className='col' style={{ ...doubleCardStyles }}>
                    <Card className='rounded-4 shadow h-100' style={{ backgroundColor: "#17296B" }}>
                        <Card.Body className='p-2 text-center'>
                            <Row className='d-flex justify-content-center'>
                                <Col className='d-flex justify-content-start align-items-center gap-2 pb-2 text-white'>
                                    <FaPencilAlt className='fs-5' />
                                    <b style={{ fontSize: "14px" }}>Tarefas Pendentes</b>
                                </Col>
                            </Row>
                            <Row className='d-flex justify-content-center flex-column mt-2'>
                                <Col className='col-12 d-flex justify-content-center'>
                                    <FaCheck className='fs-3 text-white' />
                                </Col>
                                <Col className='col-12 d-flex justify-content-center mt-2 text-white'>
                                    <b className='fs-5'>8 / </b><span style={{ color: "#a0aabf" }}>&nbsp;34</span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col className='col' style={{ ...doubleCardStyles }}>
                    <Card className='rounded-4 shadow h-100' style={{ backgroundColor: "#17296B" }}>
                        <Card.Body className='p-2 text-center'>
                            <Row className='d-flex justify-content-center'>
                                <Col className='d-flex justify-content-start align-items-center gap-2 pb-2 text-white'>
                                    <FaPencilAlt className='fs-5' />
                                    <b style={{ fontSize: "14px" }}>Tarefas Concluídas</b>
                                </Col>
                            </Row>
                            <Row className='d-flex justify-content-center flex-column mt-2'>
                                <Col className='col-12 d-flex justify-content-center'>
                                    <FaInfoCircle className='fs-3 text-white' />
                                </Col>
                                <Col className='col-12 d-flex justify-content-center mt-2 text-white'>
                                    <b>17 concluídas</b>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default MobileHome;
